import { Box, Stack, Typography } from "@mui/material";
import { CheckBox, Drawer, Tooltip } from "components";
import {
  useCreateCustomerUserOnBoarding,
  useGetCustomerSsoDetails,
  useNonPersistantMainStore,
  useUploadCustomerBatchFile,
  useUploadCustomerUserBatchFile,
} from "controller";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { SsoAddDrawer } from "shared";
import { addMixpanelEvent, debugConsole } from "utilz/helpers";
import {
  AddCustomerUsersDrawer,
} from "pages/modules/customer/users/components/add-users";
import {
  AddUserManualComponent,
  BulkUploadUsersComponent,
  StepOneCreateCustomer,
  StepThreeAddUsers,
} from "./onBoardingDrawerComponents";
import { BulkUploadCustomers } from "./onBoardingDrawerComponents/BulkUploadCustomers";
import { StepZeroCustomersSelection } from "./onBoardingDrawerComponents/StepZeroCustomersSelection";
import type { NewUser } from "./types";

export type CustomerEssential = {
  mapping_id: string;
  customer_name: string;
  email?: string;
};

function CustomerOnBoardingDrawer({
  open,
  onClose,
  callingFrom,
  onBoarding,
  setUserCount,
  setCurrentStepProp,
  customerEssentialData,
  customerandUserAdded,
  isMspOnboarding,
  customerOnboardingType,
}: {
  open: boolean;
  onClose: (e?: any, type?: "save" | "cancel") => void;
  firstCustomer?: boolean;
  callingFrom?: string;
  onBoarding?: string;
  setUserCount?: any;
  setCurrentStepProp?: number;
  customerEssentialData?: CustomerEssential;
  customerandUserAdded?: (customerId: string) => void;
  isMspOnboarding?: boolean;
  customerOnboardingType?: "custom" | "google" | "office365" | null;
}) {
  const mspFlags = useNonPersistantMainStore((state) => state.mspFlags);

  const [currentStep, setCurrentStep] = useState(
    setCurrentStepProp ?? (onBoarding === "customer" ? 0 : 1),
  );
  const [customerCreationMethod, setCustomerCreationMethod] = useState<
    "manual" | "bulk" | null
  >(null);
  const [userAddMethod, setUserAddMethod] = useState<number | null>(null);
  const [SsoDrawerOpen, setSsoDrawerOpen] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [newCustomerData, setNewCustomerData] = useState<any>(
    customerEssentialData || {},
  );
  const [newUsers, setNewUsers] = React.useState<NewUser[]>([]);
  const [onBoardingState, setOnBoardingState] = useState(onBoarding);
  const [userErrors, setUserErrors] = useState([]);
  const [bulkCustomersFileUploadState, setBulkCustomersFileUploadState] =
    useState({
      isSuccess: "pending",
      errorMessages: [],
      uploadedFiles: [],
      fileToBeUploaded: null,
      key: "",
    });
  const [bulkUsersFileUploadState, setBulkUsersFileUploadState] = useState({
    isSuccess: "pending",
    errorMessages: [],
    uploadedFiles: [],
    fileToBeUploaded: null,
    key: "",
  });
  const [isBulkUsersSendInvite, setIsBulkUsersSendInvite] = useState(false);
  const [isManualUserSendInvite, setIsManualUserSendInvite] = useState(
    mspFlags?.is_customer_invite_enabled ?? true,
  );

  useEffect(() => {
    setIsManualUserSendInvite(mspFlags?.is_customer_invite_enabled ?? true);
  }, [mspFlags]);

  const {
    mutateAsync: addBulkCustomers,
    isLoading: isAddBulkCustomersLoading,
  } = useUploadCustomerBatchFile();
  const { mutateAsync: uploadBulkUsers, isLoading: isAddBulkUsersLoading } =
    useUploadCustomerUserBatchFile();

  const uploadBulkUserFile = async (file: File) => {
    try {
      const result = await uploadBulkUsers({
        customerId: newCustomerData?.mapping_id || "",
        file,
        sendInvite: isBulkUsersSendInvite,
      });
      if (result?.errors) {
        if (Array.isArray(result?.errors) && result?.errors.length > 0) {
          setBulkUsersFileUploadState((prev: any) => ({
            ...prev,
            isSuccess: "failed",
            errorMessages: result.errors,
          }));
        }
      } else {
        toast.success("File uploaded successfully!");
        setBulkUsersFileUploadState((prev: any) => ({
          ...prev,
          isSuccess: "success",
          errorMessages: [],
          fileToBeUploaded: null,
          uploadedFiles: prev.fileToBeUploaded
            ? [...prev.uploadedFiles, prev.fileToBeUploaded]
            : [...prev.uploadedFiles],
        }));
        onClose(undefined, "save");
      }
    } catch (error) {
      toast.error(
        // @ts-ignore
        error?.message || "User upload failed.",
      );
    }
  };

  const addUserRef = useRef<any>(null);

  // const id = "b332c701-69e6-4294-a9e7-1cab3b65d437";
  const { data: ssoDetails, isLoading: isSsoDetailsLoading } =
    useGetCustomerSsoDetails(newCustomerData?.mapping_id || "");

  useEffect(() => {
    setOnBoardingState(onBoarding);
  }, [onBoarding]);

  const {
    mutateAsync: createCustomerUser,
    isLoading: isCreateCustomerUserLoading,
  } = useCreateCustomerUserOnBoarding();

  const onSubmitHandler = async () => {
    if (currentStep === 1 && customerCreationMethod === "bulk") {
      await handleBulkCustomerUpload();
      return;
    }
    if (currentStep === 4 && userAddMethod === 1) {
      await handleManualUserCreation();
      return;
    }
    if (currentStep === 4 && userAddMethod === 2) {
      await handleBulkUserUpload();
      if (customerandUserAdded) {
        customerandUserAdded(newCustomerData?.mapping_id);
      }
      return;
    }
  };

  const handleBulkCustomerUpload = async () => {
    if (!bulkCustomersFileUploadState?.fileToBeUploaded) {
      toast.error("Please select a file to upload");
      return;
    }
    try {
      const result = await addBulkCustomers(
        bulkCustomersFileUploadState.fileToBeUploaded,
      );
      if (result?.errors) {
        setBulkCustomersFileUploadState((prev) => ({
          ...prev,
          isSuccess: "failed",
          errorMessages: result.errors,
        }));
      } else {
        toast.success("Customers uploaded successfully!");
        setBulkCustomersFileUploadState((prev) => ({
          ...prev,
          isSuccess: "success",
          errorMessages: [],
          fileToBeUploaded: null,
          uploadedFiles: prev.fileToBeUploaded
            ? [...prev.uploadedFiles, prev.fileToBeUploaded]
            : [...prev.uploadedFiles],
        }));
        onClose(undefined, "save");
      }
    } catch (error) {
      toast.error(
        // @ts-ignore
        error?.message || "Customer upload failed.",
      );
    }
  };

  const handleManualUserCreation = async () => {
    const userObj = await addUserRef.current?.onAddUserClick();
    const isAddNewUserInputFieldError = userObj === null;
    const userList = userObj?.userList;

    if (isAddNewUserInputFieldError || !userList) {
      return;
    }

    if (userList.length > 0) {
      try {
        const result = await createCustomerUser({
          customerId: newCustomerData?.mapping_id,
          users: userList,
          send_invite: isManualUserSendInvite,
        });

        if (result.message === "Email validation failed") {
          setUserErrors(result?.data?.errors);
          return;
        }

        if (result.success) {
          addMixpanelEvent({ event: "create_user" });
          toast.success("User added successfully");
          onClose(undefined, "save");
        }
      } catch (error) {
        debugConsole(error);
      }
    } else {
      if (onBoardingState === "customer") {
        onClose(undefined, "save");
      } else {
        toast.error("Please add at least one user");
      }
    }
  };

  const handleBulkUserUpload = async () => {
    if (!bulkUsersFileUploadState?.fileToBeUploaded) {
      toast.error("Please select a file to upload");
      return;
    }
    uploadBulkUserFile(bulkUsersFileUploadState.fileToBeUploaded);
  };

  // const getPercentage = ({
  //   completed,
  //   totalTask,
  // }: {
  //   completed: number;
  //   totalTask: number;
  // }) => {
  //   if (totalTask === 0 || completed === 0) {
  //     return 0;
  //   } else {
  //     return Math.trunc((completed / totalTask) * 100);
  //   }
  // };

  const onBackClick = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // The redesigned manual step renders its own <Drawer>, so it replaces this
  // component's drawer entirely rather than re-skinning its chrome. Back returns
  // to the method cards (step 3) exactly as the legacy step did.
  const isManualUserStep = currentStep === 4 && userAddMethod === 1;

  return (
    <Box>
      {isManualUserStep ? (
        <AddCustomerUsersDrawer
          open={open}
          customerId={newCustomerData?.mapping_id || ""}
          customerName={newCustomerData?.customer_name || ""}
          onBack={() => setCurrentStep(3)}
          onClose={onClose}
        />
      ) : (
        <Drawer
        open={open}
        onClose={() => {
          onClose();
        }}
        header={<></>}
        // currentStep === 1 &&
        //     (onBoardingState === "customer" ? (
        //       customerCreationMethod === "bulk"
        footer={
          currentStep === 1 &&
          onBoardingState === "customer" &&
          customerCreationMethod === "bulk" ? null : currentStep ===
            4 ? undefined : (
            <></>
          )
        }
        primaryButtonText={
          currentStep === 1 &&
          onBoardingState === "customer" &&
          customerCreationMethod === "bulk"
            ? "Finish"
            : isMspOnboarding
              ? "Save"
              : "Finish"
        }
        onPrimaryButtonClick={onSubmitHandler}
        isLoading={isCreateCustomerUserLoading || isAddBulkUsersLoading}
        secondaryButtonText="Back"
        secondaryButtonSx={{
          display:
            currentStep === 1 &&
            onBoardingState === "customer" &&
            customerCreationMethod === "bulk"
              ? "none"
              : "flex",
        }}
        isPrimaryButtonDisabled={
          bulkCustomersFileUploadState?.errorMessages?.length > 0
        }
        onSecondaryButtonClick={onBackClick}
        footerLeftActionComponent={
          currentStep === 4 ? (
            <Tooltip
              title="If unchecked, the user account will still be created, but the end user won't receive an email to set their password to access customer portal and apps"
              placement="top-start"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <CheckBox
                  checked={
                    userAddMethod === 1
                      ? isManualUserSendInvite
                      : isBulkUsersSendInvite
                  }
                  onChange={() => {
                    if (userAddMethod === 1) {
                      setIsManualUserSendInvite((prev: boolean) => !prev);
                    } else {
                      setIsBulkUsersSendInvite((prev: boolean) => !prev);
                    }
                  }}
                />
                <Typography variant="body-medium">
                  Send Customer Portal Invite
                </Typography>
              </Box>
            </Tooltip>
          ) : undefined
        }
      >
        <Stack
          sx={{
            width:
              (currentStep === 1 &&
                onBoardingState === "customer" &&
                customerCreationMethod === "bulk") ||
              (currentStep === 4 && userAddMethod === 2)
                ? "764px"
                : "540px",
            pt: "24px",
            height: "97%",
            display: "flex",
            alignItems: "center",
          }}
        >
          {currentStep === 0 && onBoardingState === "customer" && (
            <StepZeroCustomersSelection
              setCustomerCreationMethod={setCustomerCreationMethod}
              setCurrentStep={setCurrentStep}
            />
          )}
          {currentStep === 1 &&
            (onBoardingState === "customer" ? (
              customerCreationMethod === "bulk" ? (
                <BulkUploadCustomers
                  fileUploadState={bulkCustomersFileUploadState}
                  setFileUploadState={setBulkCustomersFileUploadState}
                  isAddBulkCustomersLoading={isAddBulkCustomersLoading}
                />
              ) : (
                <StepOneCreateCustomer
                  onClose={onClose}
                  setCurrentStep={() => {
                    if (isMspOnboarding) {
                      if (customerOnboardingType !== "custom") {
                        setSsoDrawerOpen({
                          open: true,
                          mode: "create",
                          data: null,
                        });
                        setCurrentStep(3);
                      } else {
                        setCurrentStep(3);
                      }
                    } else {
                      setCurrentStep(2);
                    }
                  }}
                  setNewCustomerMappingId={setNewCustomerData}
                />
              )
            ) : (
              <StepThreeAddUsers
                onClose={onClose}
                setCurrentStep={setCurrentStep}
                setUserAddMethod={setUserAddMethod}
                setSsoDrawerOpen={setSsoDrawerOpen}
                onBoarding={
                  onBoardingState === "customer" ? "customer" : "user"
                }
                newCustomerData={newCustomerData}
                isMspOnboarding={isMspOnboarding}
                setOnBoardingState={setOnBoardingState}
                setNewCustomerData={setNewCustomerData}
              />
            ))}
          {/* {currentStep === 2 && (
            <StepTwoCreateCustomerSuccess
              onClose={onClose}
              setCurrentStep={setCurrentStep}
              newCustomerData={newCustomerData}
              callingFrom={callingFrom}
            />
          )} */}
          {(currentStep === 2 || currentStep === 3) && (
            <StepThreeAddUsers
              onClose={onClose}
              setCurrentStep={setCurrentStep}
              setUserAddMethod={setUserAddMethod}
              setSsoDrawerOpen={setSsoDrawerOpen}
              onBoarding={onBoardingState === "customer" ? "customer" : "user"}
              newCustomerData={newCustomerData}
              isMspOnboarding={isMspOnboarding}
              setOnBoardingState={setOnBoardingState}
              setNewCustomerData={setNewCustomerData}
            />
          )}
          {currentStep === 4 && userAddMethod === 1 && (
            <AddUserManualComponent
              setNewUsers={setNewUsers}
              newUsers={newUsers}
              userErrors={userErrors}
              setUserErrors={setUserErrors}
              ref={addUserRef}
            />
          )}
          {currentStep === 4 && userAddMethod === 2 && (
            <BulkUploadUsersComponent
              fileUploadState={bulkUsersFileUploadState}
              setFileUploadState={setBulkUsersFileUploadState}
              isAddBulkUsersLoading={isAddBulkUsersLoading}
            />
          )}
        </Stack>
      </Drawer>
      )}
      <SsoAddDrawer
        // this open boolean value is not used anywhere
        // open={currentStep === 4 && userAddMethod === 3}
        onClose={() => {
          setSsoDrawerOpen({ open: false, mode: "create", data: null });
          if (isMspOnboarding) {
            /// when user click on finish button in sso drawer then close the sso drawer and submit the form
            onSubmitHandler();
          }
        }}
        isMspOnboarding={isMspOnboarding}
        drawerData={SsoDrawerOpen}
        ssoDetails={ssoDetails}
        isSsoDetailsLoading={isSsoDetailsLoading}
        customerId={newCustomerData?.mapping_id}
        onSwitchToManualAdd={() => {
          /// when user click on would you like to add manually button switch close the sso drawer and open the manual add user options drawer
          setSsoDrawerOpen({ open: false, mode: "create", data: null });
          setUserAddMethod(1);
          setCurrentStep(3);
        }}
      />
    </Box>
  );
}

export { CustomerOnBoardingDrawer };
